/**
 * Session lifecycle for the single-page karcis booth (`bahelastruk`).
 *
 * WHY THIS EXISTS
 *
 * Every other brand creates its session inside a `startSession` handler on
 * Welcome and then navigates into the multi-page capture flow. The karcis booth
 * has no such handoff — welcome, payment, capture, print and result are five
 * stages of one component — so nothing ever ran the equivalent code, and
 * `sessionState.id` stayed at its initial `''` for the entire life of the
 * process.
 *
 * An empty session id is a silent no-op in both directions:
 *
 *   - `stateUploader.uploadSessionState` bails on `if (!id) return`, so the
 *     `uploadSessionState()` the page called on mount never POSTed anything.
 *     Both karcis projects had zero session documents all-time.
 *   - `sessionState.logActivity` drops any entry whose session id is empty
 *     before it reaches the per-session buffer, so nothing was ever queued for
 *     `/api/activity-logs` either. Activity logs hang off a session, so the
 *     remote-print successes and failures logged from `utils/printUtils` were
 *     discarded in the renderer and never reached the backend.
 *
 * The net effect was a booth that was invisible from the server: no revenue
 * attribution, no print telemetry, nothing to debug a field report against.
 *
 * This module holds the pure shape of a karcis session — what each point in
 * the cycle records — with no store or browser dependency, so it is testable on
 * its own. The store wiring that calls these lives in `karcisSessionLifecycle`.
 */

import type { DeepPartial, SessionStateData, TransactionStatus } from '@renderer/types/sessionState'

const OBJECT_ID_RE = /^[a-f0-9]{24}$/i

/** Marks karcis sessions in activity logs and on the session document. */
export const KARCIS_CUSTOM_PATH = 'bahelastruk'

/**
 * Canonical project id for the session document: the loaded config's `_id`,
 * which is always a Mongo ObjectId. The route param is a slug on the web
 * (iPad) deployment, and POSTing a slug as `projectId` is a Cast-to-ObjectId
 * 500 server-side. Kiosk builds have `config._id === routeProjectId`, so this
 * is a no-op there.
 */
export function resolveKarcisProjectId(
  configId: string | undefined,
  routeProjectId: string | undefined
): string {
  if (typeof configId === 'string' && OBJECT_ID_RE.test(configId)) return configId
  return routeProjectId || ''
}

export interface KarcisSessionStartParams {
  sessionId: string
  projectId: string
  userId: string
  startedAt: Date
  /** Ticket price in the project's native currency. */
  amount: number
  filterId?: string | null
  frameId?: string | null
  /** Karcis this session will print — one photo each. See karcisVariant.ts. */
  copies?: number
}

/**
 * State patch for the beginning of one karcis cycle.
 *
 * `setSessionState` MERGES the transaction sub-object, so the payment-identity
 * fields are reset explicitly: the karcis page loops forever in one component
 * and never unmounts between customers, so without this the previous cycle's
 * `orderId` and `settlement` status would leak into the next customer's
 * session and corrupt revenue attribution — the same leak the standard Welcome
 * guards against.
 */
export function buildKarcisSessionStart({
  sessionId,
  projectId,
  userId,
  startedAt,
  amount,
  filterId,
  frameId,
  copies = 1
}: KarcisSessionStartParams): DeepPartial<SessionStateData> {
  return {
    id: sessionId,
    projectId,
    userId,
    mode: 'capture',
    timeline: {
      startTime: startedAt,
      endTime: null,
      duration: null,
      isCompleted: false
    },
    userPreferences: {
      filterId: filterId ?? null,
      frameId: frameId ?? null,
      copies,
      usePrint: true,
      useDigital: false,
      useGIF: false,
      useRecording: false,
      useVideo: false
    },
    transaction: {
      orderId: null,
      qrUrl: null,
      expiryTime: null,
      serverTime: null,
      status: 'pending',
      amount,
      discount: 0,
      printed: false
    },
    gallery: {
      id: `${9999999999999 - startedAt.getTime()}-${sessionId}`
    }
  }
}

export interface KarcisPaymentParams {
  orderId: string | null
  amount: number
  status: TransactionStatus
  qrUrl?: string | null
  expiryTime?: string | null
  serverTime?: string | null
  /**
   * When the guest actually engaged with the booth, re-stamped onto
   * `timeline.startTime`.
   *
   * Every other brand starts its session on a "Mulai" tap, so `startTime` is
   * when the guest arrived. This booth has no tap: it opens a cycle the moment
   * the previous one ends and then quotes QRs to an empty room until someone
   * turns up, which may be hours. Left alone, the first paid session of the day
   * would report a duration measured from boot. Payment is the only engagement
   * signal this flow has, so settlement is what sets the clock.
   */
  engagedAt?: Date | null
  /**
   * The voucher that paid for some or all of this session, if one did.
   *
   * `voucherId` is what actually matters downstream: `POST /api/sessions`
   * decrements the voucher's `limitUsage` when a settled session arrives
   * carrying one, and that is the ONLY thing that burns a quota on a session
   * that never reaches the gateway. A free session recorded without it stays
   * redeemable forever, which for the Bilik Kotak reward would mean one code
   * good for unlimited free karcis.
   *
   * `discount` is rupiah off the price, never the Bahela checkout's
   * negative-means-percentage encoding — this booth writes the transaction
   * itself, so it writes the money.
   */
  discount?: number
  voucherCode?: string
  voucherId?: string
  voucherType?: 'fixed' | 'percentage' | 'cash'
  voucherValue?: number
}

/**
 * State patch for a payment reaching a new state (quoted, settled, expired).
 *
 * `status` is deliberately restricted to the values the server's Session
 * schema enumerates for `transaction.status`; anything else is dropped on
 * write.
 */
export function buildKarcisPayment({
  orderId,
  amount,
  status,
  qrUrl = null,
  expiryTime = null,
  serverTime = null,
  engagedAt = null,
  discount,
  voucherCode,
  voucherId,
  voucherType,
  voucherValue
}: KarcisPaymentParams): DeepPartial<SessionStateData> {
  return {
    ...(engagedAt ? { timeline: { startTime: engagedAt } } : {}),
    transaction: {
      orderId,
      amount,
      status,
      qrUrl,
      expiryTime,
      serverTime,
      // Omitted rather than nulled when absent: `setSessionState` merges the
      // transaction sub-object, so writing `voucherCode: undefined` on every
      // quote would wipe a voucher applied a moment earlier. The TV booth,
      // which can never pass any of these, gets the exact object it always did.
      ...(discount !== undefined ? { discount } : {}),
      ...(voucherCode ? { voucherCode } : {}),
      ...(voucherId ? { voucherId } : {}),
      ...(voucherType ? { voucherType } : {}),
      ...(voucherValue !== undefined ? { voucherValue } : {})
    }
  }
}

export interface KarcisSessionFinishParams {
  startedAt: Date | null
  endedAt: Date
  printed: boolean
}

/**
 * State patch that closes a karcis cycle.
 *
 * `isCompleted` tracks `printed` rather than being unconditionally true: the
 * cycle also ends when the supervisor writes off a stuck stage or the operator
 * navigates away, and marking those complete would report a booth that ate
 * someone's Rp 5.000 as a successful session. An `endTime` with
 * `isCompleted: false` is exactly the shape a failed session should have.
 */
export function buildKarcisSessionFinish({
  startedAt,
  endedAt,
  printed
}: KarcisSessionFinishParams): DeepPartial<SessionStateData> {
  return {
    timeline: {
      endTime: endedAt,
      duration: startedAt ? endedAt.getTime() - startedAt.getTime() : null,
      isCompleted: printed
    },
    transaction: {
      printed
    }
  }
}
